import { Button, FocusRing } from '@change/ds-core-ui';
import { CloseIcon, SearchIcon } from '@change/ds-icons';

import { Translate } from '@change-corgi/core/react/i18n';

import { useCustomSearchBox } from './hooks/useCustomSearchBox';
import { useTranslations } from './hooks/useTranslations';

export const CustomSearchBox: React.FC = () => {
	const {
		data: { isLoading, inputRef, inputValue },
		actions: { setQuery, clearQuery, submitSearch },
	} = useCustomSearchBox();

	const {
		data: { placeholder, resetLabel },
	} = useTranslations();

	return (
		<div className="container w-full">
			<div className="mx-auto w-full md:w-8/12">
				<form
					action=""
					role="search"
					noValidate
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						submitSearch();
						inputRef.current?.blur();
					}}
					onReset={(event) => {
						event.preventDefault();
						event.stopPropagation();
						clearQuery();
						inputRef.current?.focus();
					}}
					className="relative flex w-full items-center justify-center gap-2"
					aria-busy={isLoading}
				>
					<div className="relative w-full">
						<label htmlFor="search-input" className="sr-only">
							<Translate value="corgi.search.search_box.submit" />
						</label>
						<FocusRing>
							<input
								id="search-input"
								ref={inputRef}
								autoComplete="off"
								autoCorrect="off"
								autoCapitalize="off"
								placeholder={placeholder}
								spellCheck={false}
								maxLength={512}
								type="search"
								value={inputValue}
								onChange={(e) => {
									setQuery(e.target.value);
								}}
								className="h-14 w-full rounded-medium border-[1px] border-solid border-stroke-strong bg-fill-inverse-strong px-10 py-4 text-size-body-large text-typography-strong placeholder-typography-disabled focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-stroke-focus"
								data-qa="search-input"
							/>
						</FocusRing>
						<SearchIcon className="absolute left-3 top-0 h-14 text-typography-strong" />
						{!isLoading && inputValue && <ClearButton resetLabel={resetLabel} />}
					</div>

					<Button
						variant="primary"
						type="submit"
						className="hidden md:block"
						disabled={isLoading}
						data-testid="submit-button"
						data-qa="search-input-submit-button"
					>
						<Translate value="corgi.search.search_box.submit" />
					</Button>
				</form>
			</div>
		</div>
	);
};

const ClearButton: React.FC<{ resetLabel: string }> = ({ resetLabel }) => (
	<div className="absolute bottom-0 right-0 top-0 flex h-14 flex-col justify-center px-2">
		<FocusRing>
			<button
				type="reset"
				aria-label={resetLabel}
				className="hover:text-typography-strong-hovered flex h-10 w-10 items-center justify-center rounded-full bg-fill-inverse-strong text-typography-strong hover:bg-fill-hover focus:bg-fill-hover focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-stroke-focus"
			>
				<CloseIcon aria-hidden="true" />
				<span className="sr-only">
					<Translate value="corgi.search.search_box.reset" />
				</span>
			</button>
		</FocusRing>
	</div>
);
